/**
 * BP-21 T7: kiosk mode, and the lighting calibration that makes it worth
 * having.
 *
 * **§14 item 2 is applied**: one tablet, one stand, one lamp. Kiosk mode
 * assumes the light does not move and says so in its own copy — which is
 * the only honest thing to say, because the whole reason the salon's render
 * can be better than a phone's is that somebody fixed the light.
 *
 * **What calibration actually does.** A camera's auto white balance guesses
 * what in the frame is meant to be neutral. Under a salon's warm LED it
 * guesses wrong on a hand, because skin is not neutral, and the shade comes
 * out several hundred kelvin off — which is exactly the error a client
 * notices, because she is holding the bottle. Showing the camera something
 * that *is* neutral — the grey card on the stand — and recording the gains
 * that make it read neutral removes the guess.
 *
 * **The gains are von Kries, in linear light.** Per-channel scaling is the
 * crude version of chromatic adaptation and it is the right crude version
 * here: the error being corrected is an illuminant, which is what per-
 * channel scaling models.
 *
 * Nothing here samples a camera. It takes the three numbers the sample came
 * to and gives back three gains, which is the part that can be wrong in a
 * way a test can catch.
 */

import type { Rgb } from '@/lib/ar';

import type { ArMode } from './schemas';

/** `?mode=tablet` puts the screen in kiosk mode. Anything else is a phone. */
export function modeFromSearch(search: string): ArMode {
  return new URLSearchParams(search).get('mode') === 'tablet' ? 'tablet' : 'phone';
}

/** Per-channel gains that take the sampled card to neutral. */
export interface WhiteBalance {
  r: number;
  g: number;
  b: number;
}

/** No correction at all — what a phone uses, and what a failed sample gives. */
export const NEUTRAL: WhiteBalance = { r: 1, g: 1, b: 1 };

/**
 * Where a usable grey-card sample has to sit, in linear light.
 *
 * Below the floor the sample is in shadow and its channel ratios are mostly
 * sensor noise; above the ceiling one channel has clipped, and a clipped
 * channel's true value is unknown — calibrating from it bakes the clipping
 * into every shade for the rest of the day.
 */
export const SAMPLE_FLOOR = 0.03;
export const SAMPLE_CEILING = 0.95;

/** Why a calibration was refused, for copy that tells the salon what to do. */
export type CalibrationProblem = 'too_dark' | 'too_bright';

export type Calibration =
  | { balance: WhiteBalance; problem?: undefined }
  | { balance?: undefined; problem: CalibrationProblem };

/**
 * Gains from one grey-card sample.
 *
 * Normalised on green, which is where every camera puts most of its
 * sensitivity and therefore its least noisy channel. Normalising on the
 * mean instead would move the overall brightness as well as the colour, and
 * brightness is the lamp's business, not the calibration's.
 */
export function calibrate(sample: Rgb): Calibration {
  const channels = [sample.r, sample.g, sample.b];
  if (channels.some((value) => value < SAMPLE_FLOOR)) {
    return { problem: 'too_dark' };
  }
  if (channels.some((value) => value > SAMPLE_CEILING)) {
    return { problem: 'too_bright' };
  }
  return { balance: { r: sample.g / sample.r, g: 1, b: sample.g / sample.b } };
}

/** Apply the gains to a colour. Clamped: a gain above 1 can take a channel over. */
export function applyBalance(colour: Rgb, balance: WhiteBalance): Rgb {
  return {
    r: Math.min(colour.r * balance.r, 1),
    g: Math.min(colour.g * balance.g, 1),
    b: Math.min(colour.b * balance.b, 1),
  };
}
